import type { ChatbotIntent, ChatbotIntentId, ChatbotTopic, QueryProfiles } from "../types";

const STOP_WORDS = new Set([
  "a",
  "an",
  "and",
  "are",
  "as",
  "at",
  "be",
  "by",
  "can",
  "could",
  "do",
  "does",
  "find",
  "for",
  "from",
  "get",
  "give",
  "help",
  "how",
  "i",
  "if",
  "in",
  "into",
  "is",
  "it",
  "me",
  "more",
  "need",
  "of",
  "on",
  "or",
  "quick",
  "resources",
  "resource",
  "should",
  "show",
  "that",
  "the",
  "their",
  "this",
  "to",
  "what",
  "when",
  "where",
  "which",
  "who",
  "why",
  "will",
  "with",
  "you",
  "your"
]);

const GENERIC_TOPIC_TERMS = new Set([
  "build",
  "customer",
  "founder",
  "growth",
  "key",
  "market",
  "objective",
  "product",
  "startup",
  "strategy",
  "system",
  "user"
]);

const FALLBACK_CANONICAL_TERMS: Record<string, string> = {
  retention: "retain",
  retaining: "retain",
  retained: "retain",
  launching: "launch",
  launched: "launch",
  launches: "launch",
  building: "build",
  builds: "build",
  built: "build",
  validating: "validate",
  validated: "validate",
  validates: "validate",
  sells: "sales",
  sell: "sales",
  selling: "sales",
  sold: "sales",
  users: "user",
  customers: "customer",
  founders: "founder",
  assumptions: "assumption",
  schools: "school",
  districts: "district",
  teachers: "teacher",
  educators: "educator"
};

const DEFAULT_DOMAIN_TERMS = [
  "startup",
  "customer",
  "user",
  "founder",
  "mvp",
  "product",
  "market",
  "sales",
  "school",
  "district",
  "teacher",
  "educator",
  "edtech",
  "education",
  "procurement",
  "revenue",
  "pricing",
  "growth",
  "fundraising",
  "investor",
  "team",
  "operation",
  "retention",
  "acquisition",
  "strategy",
  "objective",
  "enabler"
];

export type ParsedChatQuery = {
  original: string;
  normalized: string;
  normalizedWords: string[];
  terms: string[];
  phrases: string[];
  expandedTerms: string[];
};

export type QueryMatchContext = {
  query: ParsedChatQuery;
  topicScores: Map<string, number>;
  objectiveScores: Map<string, number>;
  facetScores: Map<string, number>;
  matchedTopics: string[];
  matchedObjectiveCodes: string[];
  matchedFacets: string[];
  hasDomainSignal: boolean;
};

type PhraseProfile = {
  aliases: string[];
  painPhrases: string[];
  actionTerms?: string[];
};

export function parseChatQuery(query: string, topics: ChatbotTopic[], profiles?: QueryProfiles): ParsedChatQuery {
  const normalized = normalizeText(query, profiles);
  const normalizedWords = normalized.split(" ").filter(Boolean);
  const terms = unique(normalizedWords.filter((term) => term.length > 1 && !STOP_WORDS.has(term)));
  const phrases = buildPhrases(normalizedWords);
  const expandedTerms = unique([...terms, ...expandTerms(normalized, terms, topics, profiles)]);
  return { original: query, normalized, normalizedWords, terms, phrases, expandedTerms };
}

export function normalizeText(value: string, profiles?: QueryProfiles): string {
  return value
    .normalize("NFKD")
    .toLowerCase()
    .replace(/[\u2010-\u2015]/g, "-")
    .replace(/[^a-z0-9.]+/g, " ")
    .split(" ")
    .map((term) => normalizeTerm(term, profiles))
    .filter(Boolean)
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();
}

export function detectIntent(query: ParsedChatQuery, intents: ChatbotIntent[]): ChatbotIntentId {
  const matched = intents
    .map((intent) => ({
      id: intent.id,
      score: intent.patterns.filter((pattern) => query.normalized.includes(normalizeText(pattern))).length
    }))
    .filter((intent) => intent.score > 0)
    .sort((a, b) => b.score - a.score);

  if (matched[0]) {
    return matched[0].id;
  }

  if (/^(what|how|why|when|where)\b/.test(query.normalized) || query.terms.length >= 2) {
    return "topic_search";
  }

  return "unknown";
}

export function buildQueryMatchContext(
  query: ParsedChatQuery,
  topics: ChatbotTopic[],
  profiles: QueryProfiles
): QueryMatchContext {
  const topicScores = scoreTopics(query, topics, profiles);
  const objectiveScores = scoreObjectives(query, profiles);
  const facetScores = scoreFacets(query, profiles);

  for (const [code, score] of objectiveScores) {
    const objectiveProfile = profiles.objectives[code];
    for (const topicId of objectiveProfile?.linkedTopicIds ?? []) {
      addScore(topicScores, topicId, Math.min(6, Math.ceil(score / 3)));
    }
  }

  for (const [topicId, score] of topicScores) {
    const topicProfile = profiles.topics[topicId];
    for (const code of topicProfile?.linkedObjectiveCodes ?? []) {
      addScore(objectiveScores, code, Math.min(5, Math.ceil(score / 4)));
    }
  }

  const matchedTopics = sortedKeys(topicScores).slice(0, 2);
  const matchedObjectiveCodes = sortedKeys(objectiveScores).slice(0, 4);
  const matchedFacets = sortedKeys(facetScores);
  const hasDomainSignal =
    matchedTopics.length > 0 ||
    matchedObjectiveCodes.length > 0 ||
    matchedFacets.length > 0 ||
    hasDomainTerm(query, profiles);

  return {
    query,
    topicScores,
    objectiveScores,
    facetScores,
    matchedTopics,
    matchedObjectiveCodes,
    matchedFacets,
    hasDomainSignal
  };
}

export function shouldUseFallback(matchContext: QueryMatchContext): boolean {
  return !matchContext.hasDomainSignal;
}

export function matchTopics(query: ParsedChatQuery, topics: ChatbotTopic[], profiles?: QueryProfiles): string[] {
  if (profiles) {
    return buildQueryMatchContext(query, topics, profiles).matchedTopics;
  }

  return topics
    .map((topic) => {
      const topicText = normalizeText([topic.label, topic.description, ...topic.synonyms].join(" "));
      const topicTerms = new Set(topicText.split(" ").filter(Boolean));
      const phraseMatches = topicPhraseMatches(query.normalized, topic);
      const specificTermMatches = query.terms.filter((term) => !GENERIC_TOPIC_TERMS.has(term) && topicTerms.has(term)).length;
      const score = phraseMatches * 6 + specificTermMatches;
      return { id: topic.id, score };
    })
    .filter((topic) => topic.score > 0)
    .sort((a, b) => b.score - a.score || a.id.localeCompare(b.id))
    .slice(0, 2)
    .map((topic) => topic.id);
}

function scoreTopics(query: ParsedChatQuery, topics: ChatbotTopic[], profiles: QueryProfiles): Map<string, number> {
  const output = new Map<string, number>();
  for (const topic of topics) {
    const profile = profiles.topics[topic.id];
    const profileTerms = [topic.label, topic.description, ...topic.synonyms];
    if (profile) {
      profileTerms.push(...profile.aliases, ...profile.painPhrases, ...profile.actionTerms);
    }

    const topicText = normalizeText(profileTerms.join(" "), profiles);
    const topicTerms = new Set(topicText.split(" ").filter(Boolean));
    const phraseScore = scorePhraseProfile(query, profiles, {
      aliases: [topic.label, ...topic.synonyms, ...(profile?.aliases ?? [])],
      painPhrases: profile?.painPhrases ?? [],
      actionTerms: profile?.actionTerms ?? []
    });
    const specificTermMatches = query.terms.filter((term) => !GENERIC_TOPIC_TERMS.has(term) && topicTerms.has(term)).length;
    const score = phraseScore + specificTermMatches;
    if (score > 0) {
      output.set(topic.id, score);
    }
  }
  return output;
}

function scoreObjectives(query: ParsedChatQuery, profiles: QueryProfiles): Map<string, number> {
  const output = new Map<string, number>();
  for (const [code, profile] of Object.entries(profiles.objectives)) {
    const codeMatch = query.normalized.includes(`objective ${normalizeText(code, profiles)}`) || query.terms.includes(normalizeText(code, profiles));
    const phraseScore = scorePhraseProfile(query, profiles, profile);
    const objectiveText = normalizeText([...profile.aliases, ...profile.painPhrases].join(" "), profiles);
    const objectiveTerms = new Set(objectiveText.split(" ").filter(Boolean));
    const termMatches = query.terms.filter((term) => !GENERIC_TOPIC_TERMS.has(term) && objectiveTerms.has(term)).length;
    const score = (codeMatch ? 12 : 0) + phraseScore + termMatches;
    if (score > 0) {
      output.set(code, score);
    }
  }
  return output;
}

function scoreFacets(query: ParsedChatQuery, profiles: QueryProfiles): Map<string, number> {
  const output = new Map<string, number>();
  for (const [facetId, facet] of Object.entries(profiles.facets)) {
    const score = scoreTermsAndPhrases(query, facet.terms, profiles);
    if (score > 0) {
      output.set(facetId, score);
    }
  }
  return output;
}

function scorePhraseProfile(query: ParsedChatQuery, profiles: QueryProfiles, profile: PhraseProfile): number {
  const aliasScore = scoreTermsAndPhrases(query, profile.aliases, profiles) * 6;
  const painScore = scoreTermsAndPhrases(query, profile.painPhrases, profiles) * 8;
  const actionScore = scoreTermsAndPhrases(query, profile.actionTerms ?? [], profiles) * 3;
  return aliasScore + painScore + actionScore;
}

function scoreTermsAndPhrases(query: ParsedChatQuery, values: string[], profiles: QueryProfiles): number {
  let score = 0;
  for (const value of values) {
    const normalizedValue = normalizeText(value, profiles);
    if (!normalizedValue) {
      continue;
    }
    if (normalizedValue.includes(" ")) {
      if (query.normalized.includes(normalizedValue)) {
        score += 1;
      }
      continue;
    }
    if (query.terms.includes(normalizedValue)) {
      score += 1;
    }
  }
  return score;
}

function hasDomainTerm(query: ParsedChatQuery, profiles: QueryProfiles): boolean {
  const domainTerms = profiles.domainTerms.length > 0 ? profiles.domainTerms : DEFAULT_DOMAIN_TERMS;
  return domainTerms.some((term) => {
    const normalizedTerm = normalizeText(term, profiles);
    if (!normalizedTerm) {
      return false;
    }
    return normalizedTerm.includes(" ") ? query.normalized.includes(normalizedTerm) : query.terms.includes(normalizedTerm);
  });
}

function topicPhraseMatches(normalizedQuery: string, topic: ChatbotTopic): number {
  return [topic.label, ...topic.synonyms]
    .map((value) => normalizeText(value))
    .filter((label) => label.includes(" "))
    .filter((label) => normalizedQuery.includes(label)).length;
}

function expandTerms(normalizedQuery: string, terms: string[], topics: ChatbotTopic[], profiles?: QueryProfiles): string[] {
  const output: string[] = [];
  for (const topic of topics) {
    const profile = profiles?.topics[topic.id];
    const labels = [topic.label, ...topic.synonyms, ...(profile?.aliases ?? []), ...(profile?.painPhrases ?? [])].map((value) =>
      normalizeText(value, profiles)
    );
    if (labels.some((label) => label && normalizedQuery.includes(label))) {
      output.push(
        ...normalizeText(
          [topic.label, topic.description, ...topic.synonyms, ...(profile?.aliases ?? []), ...(profile?.actionTerms ?? [])].join(" "),
          profiles
        ).split(" ")
      );
    }
  }

  for (const profile of Object.values(profiles?.objectives ?? {})) {
    const labels = [...profile.aliases, ...profile.painPhrases].map((value) => normalizeText(value, profiles));
    if (labels.some((label) => label && normalizedQuery.includes(label))) {
      output.push(...normalizeText([...profile.aliases, ...profile.linkedTopicIds].join(" "), profiles).split(" "));
    }
  }

  return output.filter((term) => term.length > 1 && !STOP_WORDS.has(term) && !terms.includes(term));
}

function buildPhrases(words: string[]): string[] {
  const phrases: string[] = [];
  for (let size = 2; size <= 4; size += 1) {
    for (let index = 0; index <= words.length - size; index += 1) {
      phrases.push(words.slice(index, index + size).join(" "));
    }
  }
  return unique(phrases);
}

function normalizeTerm(term: string, profiles?: QueryProfiles): string {
  const canonicalTerms = { ...FALLBACK_CANONICAL_TERMS, ...(profiles?.normalization.canonicalTerms ?? {}) };
  const normalized = canonicalTerms[term] ?? term;
  return normalized
    .replace(/ization\b/g, "isation")
    .replace(/izations\b/g, "isations")
    .replace(/izing\b/g, "ising")
    .replace(/ized\b/g, "ised")
    .replace(/ize\b/g, "ise");
}

function addScore(scores: Map<string, number>, key: string, score: number): void {
  scores.set(key, (scores.get(key) ?? 0) + score);
}

function sortedKeys(scores: Map<string, number>): string[] {
  return Array.from(scores.entries())
    .filter(([, score]) => score > 0)
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([key]) => key);
}

function unique(values: string[]): string[] {
  return Array.from(new Set(values));
}
